import { Box, TextField, useTheme } from "@mui/material";
import { CronExpressionParser } from "cron-parser";
import cronstrue from "cronstrue";
import React from "react";

import { RobotoTypography } from "@/shared/components";

interface TriggerScheduleProps {
  editable: boolean;
  schedule: string;
  onScheduleChanged: (newSchedule: string) => void;
  scheduleError: string | undefined;
  onScheduleError: (newError: string | undefined) => void;
}

function getCronScheduleDescription(schedule: string): string {
  return cronstrue.toString(schedule);
}

function tryParseCronSchedule(schedule: string): string | undefined {
  try {
    // The Roboto backend supports the default Cron format with 5 parts.
    // Both cron-parser and cRonstrue support 6 (optional seconds), and
    // cron-parser alone supports < 5 (defaulting the rest). So we're
    // explicit here for consistency with the backend.
    const parts = schedule.trim().split(/\s+/);
    if (parts.length !== 5) {
      throw Error("Invalid cron expression, expected 5 fields");
    }

    CronExpressionParser.parse(schedule);
    return undefined;
  } catch (error) {
    if (error instanceof Error) {
      return error.message;
    } else if (typeof error === "string") {
      return error;
    } else {
      return "Invalid cron expression";
    }
  }
}

export function TriggerSchedule({
  editable,
  schedule,
  onScheduleChanged,
  scheduleError,
  onScheduleError,
}: TriggerScheduleProps) {
  const theme = useTheme();

  const handleScheduleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newSchedule = event.target.value;
    onScheduleChanged(newSchedule);

    const error = tryParseCronSchedule(newSchedule);
    onScheduleError(error);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {editable && (
        <TextField
          sx={{ mt: theme.spacing(1), width: "100%", maxWidth: 400 }}
          size="small"
          variant="outlined"
          label="Cron Expression"
          value={schedule}
          helperText={scheduleError}
          onChange={handleScheduleChange}
          error={scheduleError !== undefined}
        />
      )}

      <RobotoTypography
        component="span"
        variant="body2"
        style={{
          whiteSpace: "pre-wrap",
          color: theme.palette.text.secondary,
          wordBreak: "break-word",
        }}
      >
        {scheduleError ? "" : getCronScheduleDescription(schedule)}
      </RobotoTypography>
    </Box>
  );
}
